import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  Box,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LinkIcon from "@material-ui/icons/Link";
import AssistantOutlined from "@material-ui/icons/AssistantOutlined";
import Refresh from "@material-ui/icons/Refresh";
import { Alert } from "@material-ui/lab";
import Time from "../../lithic/Moment";
import config from "../../config";
import usePollRefreshStatus from "../../hooks/usePollRefreshStatus";
import { refreshProposal } from "./applicationAgentApi";
import AnswerQuestionSubform from "./slices/AnswerQuestionSubform";
import ResendInviteSubform from "./slices/ResendInviteSubform";

const actionCategories = [
  { value: "other", label: "Other" },
  { value: "id_verification_override", label: "ID Verification Override" },
  { value: "id_verification_retry", label: "ID Verification Retry" },
  { value: "income_verification_restart", label: "Income Verification Restart" },
  { value: "merged_apps", label: "Merged Applicants" },
  { value: "resent_invite", label: "Resent Invite" },
  { value: "answered_question", label: "Answered Question" },
];

const PROPOSABLE_CATEGORIES = new Set(["resent_invite", "answered_question", "no_intervention"]);

const recipientsFromProposal = (alert, proposal) => {
  if (!alert || !proposal || proposal.category !== "resent_invite") return [];
  const proposed = proposal.payload?.recipients || [];
  const coApplicants = alert.coApplicants || [];
  return proposed
    .map((pr) =>
      coApplicants.find((ca) =>
        pr.pendingMagicLinkId
          ? ca.pendingMagicLink?.id === pr.pendingMagicLinkId
          : ca.applicantId != null && ca.applicantId === pr.applicantId,
      ),
    )
    .filter((ca) => ca && ca.pendingMagicLink?.status === "pending")
    .map((ca) => ({
      applicantId: ca.applicantId,
      pendingMagicLinkId: ca.pendingMagicLink.id,
      name: ca.name,
      email: ca.email,
    }));
};

const sameRecipients = (a, b) => {
  if (a.length !== b.length) return false;
  const aIds = new Set(a.map((r) => r.pendingMagicLinkId));
  return b.every((r) => aIds.has(r.pendingMagicLinkId));
};

const STALE_REASON_MESSAGES = {
  magic_link_expired:
    "One or more proposed recipients' invites have expired since the agent generated this proposal. Please review and submit manually.",
  magic_link_consumed:
    "One or more proposed recipients have already accepted their invites since the agent generated this proposal. Please review and submit manually.",
};

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  message: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: 4,
    whiteSpace: "pre-wrap",
    maxHeight: 200,
    overflow: "auto",
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  context: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1),
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  generateSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
  proposalBanner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: "rgba(230, 0, 126, 0.08)",
    border: `1px solid ${theme.palette.primary.light}`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: 4,
    color: theme.palette.primary.dark || theme.palette.primary.main,
  },
  proposalBannerIcon: {
    color: theme.palette.primary.main,
  },
  proposalBannerText: {
    fontWeight: 600,
  },
  inlineError: {
    marginBottom: theme.spacing(2),
  },
}));

const ActionDialog = ({
  open,
  alert,
  onClose,
  onSubmit,
  onAcceptProposal,
  mcpToken,
  mcpTokenError,
}) => {
  const classes = useStyles();
  const [actionType, setActionType] = useState("intervention");
  const [actionCategory, setActionCategory] = useState("");
  const [actionDescription, setActionDescription] = useState("");
  const [suggestedMessage, setSuggestedMessage] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [inlineError, setInlineError] = useState(null);
  const [localAlertData, setLocalAlertData] = useState(null);
  const [postError, setPostError] = useState(null);

  const { isPolling, error: pollError, startPolling } = usePollRefreshStatus();

  const currentAlert = localAlertData ?? alert;

  const proposal =
    currentAlert && currentAlert.proposedAction && PROPOSABLE_CATEGORIES.has(currentAlert.proposedAction.category)
      ? currentAlert.proposedAction
      : null;

  const proposalDefaults = useMemo(
    () => ({
      message: proposal?.draftMessage || "",
      recipients: recipientsFromProposal(currentAlert, proposal),
    }),
    [currentAlert, proposal],
  );

  useEffect(() => {
    if (open && alert) {
      setLocalAlertData(null);
      setPostError(null);
      setActionType(proposal?.category === "no_intervention" ? "no_intervention" : "intervention");
      if (proposal) {
        if (proposal.category !== "no_intervention") {
          setActionCategory(proposal.category);
        } else {
          setActionCategory("");
        }
        setSuggestedMessage(proposalDefaults.message);
        setRecipients(proposalDefaults.recipients);
      } else {
        setActionCategory("");
        setSuggestedMessage("");
        setRecipients([]);
      }
      setActionDescription("");
      setSubmitting(false);
      setEditMode(false);
      setInlineError(null);
    }
  }, [open, alert?.id]);

  useEffect(() => {
    if (!localAlertData) return;
    const freshProposal =
      localAlertData.proposedAction && PROPOSABLE_CATEGORIES.has(localAlertData.proposedAction.category)
        ? localAlertData.proposedAction
        : null;
    setActionType(freshProposal?.category === "no_intervention" ? "no_intervention" : "intervention");
    if (freshProposal) {
      setActionCategory(freshProposal.category !== "no_intervention" ? freshProposal.category : "");
      setSuggestedMessage(freshProposal.draftMessage || "");
      setRecipients(recipientsFromProposal(localAlertData, freshProposal));
    } else {
      setActionCategory("");
      setSuggestedMessage("");
      setRecipients([]);
    }
    setActionDescription("");
    setEditMode(false);
    setInlineError(null);
  }, [localAlertData]);

  const handleCategoryChange = (newCategory) => {
    if (proposal && newCategory === proposal.category) {
      setActionDescription("");
      setSuggestedMessage(proposalDefaults.message);
      setRecipients(proposalDefaults.recipients);
    } else if (actionCategory) {
      setActionDescription("");
      setSuggestedMessage("");
      setRecipients([]);
    }
    setActionCategory(newCategory);
  };

  const isAnsweredQuestion =
    actionType === "intervention" && actionCategory === "answered_question";
  const isResentInvite =
    actionType === "intervention" && actionCategory === "resent_invite";

  const isProposalActive =
    proposal &&
    (proposal.category === "no_intervention"
      ? actionType === "no_intervention"
      : actionType === "intervention" && actionCategory === proposal.category);

  const formLocked = Boolean(proposal) && !editMode;

  const isEdited =
    isProposalActive &&
    editMode &&
    (suggestedMessage !== proposalDefaults.message ||
      (proposal.category === "resent_invite" &&
        !sameRecipients(recipients, proposalDefaults.recipients)));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formData = {
        actionType,
        actionCategory: actionType === "intervention" ? actionCategory : undefined,
        actionDescription,
      };
      if (isAnsweredQuestion || isResentInvite) {
        formData.message = suggestedMessage;
      }
      if (isResentInvite) {
        formData.recipients = recipients;
      }
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async () => {
    if (!proposal) return;
    setSubmitting(true);
    setInlineError(null);
    try {
      await onAcceptProposal({
        alertId: alert.id,
        proposalId: proposal.id,
      });
    } catch (err) {
      if (err && err.staleProposal) {
        setInlineError(
          STALE_REASON_MESSAGES[err.reason] ||
            "This proposal is no longer valid. Please review and submit manually.",
        );
        setEditMode(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const alertId = alert?.id;
  const refreshError = postError || pollError;

  const handleRefresh = useCallback(async () => {
    if (!alertId || !mcpToken) return;
    setPostError(null);
    try {
      await refreshProposal(mcpToken, { alertId });
    } catch (err) {
      setPostError(err.message || "Failed to trigger refresh");
      return;
    }
    startPolling(alertId, setLocalAlertData);
  }, [alertId, mcpToken, startPolling]);

  const needsMcpToken =
    actionType === "no_intervention" || isAnsweredQuestion || isResentInvite;

  const requiresDescription = !isAnsweredQuestion && !isResentInvite;

  const isValid =
    (!requiresDescription || actionDescription.trim().length > 0) &&
    (actionType === "no_intervention" || actionCategory) &&
    (!isAnsweredQuestion || suggestedMessage.trim().length > 0) &&
    (!isResentInvite ||
      (suggestedMessage.trim().length > 0 && recipients.length > 0)) &&
    (!needsMcpToken || mcpToken);

  const renderInterventionBody = () => {
    if (isAnsweredQuestion) {
      return (
        <AnswerQuestionSubform
          alert={currentAlert}
          mcpToken={mcpToken}
          suggestedMessage={suggestedMessage}
          onSuggestedMessageChange={setSuggestedMessage}
          submitting={submitting}
          disabled={formLocked}
          classes={classes}
        />
      );
    }
    if (isResentInvite) {
      return (
        <ResendInviteSubform
          alert={currentAlert}
          mcpToken={mcpToken}
          suggestedMessage={suggestedMessage}
          recipients={recipients}
          onRecipientsChange={setRecipients}
          onSuggestedMessageChange={setSuggestedMessage}
          submitting={submitting}
          disabled={formLocked}
          classes={classes}
        />
      );
    }
    return (
      <div className={classes.section}>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          label={actionType === "intervention" ? "What was done?" : "Why no action?"}
          value={actionDescription}
          onChange={(e) => setActionDescription(e.target.value)}
          required
          disabled={submitting || formLocked}
        />
      </div>
    );
  };

  const renderDialogTitle = () => (
    <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Typography variant="h6">Alert Detail</Typography>
      <Tooltip title={copied ? "Copied!" : "Copy link"}>
        <IconButton
          onClick={() => {
            const url = `${window.location.origin}/applicant-alerts?alertId=${alert.id}`;
            navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          size="small"
        >
          <LinkIcon fontSize="small" color={copied ? "primary" : "action"} />
        </IconButton>
      </Tooltip>
    </DialogTitle>
  );

  const renderKeyConcerns = () => {
    if (!alert?.keyConcerns || alert.keyConcerns.length === 0) return null;
    return (
      <div className={classes.section}>
        <Typography className={classes.label} variant="subtitle2">
          Key Concerns
        </Typography>
        <Box display="flex" flexWrap="wrap">
          {alert.keyConcerns.map((concern, i) => (
            <Chip
              key={i}
              label={concern}
              size="small"
              className={classes.chip}
            />
          ))}
        </Box>
      </div>
    );
  };

  const renderTakenAction = (action) => {
    if (!action) return null;
    return (
      <div className={classes.section}>
        <Typography className={classes.label} variant="subtitle2">
          Action Taken
        </Typography>
        <div className={classes.context}>
          <Typography variant="body2">
            <strong>Type:</strong>{" "}
            {action.actionType === "intervention" ? "Intervention" : "No Intervention"}
          </Typography>
          {action.actionCategory && (
            <Typography variant="body2">
              <strong>Category:</strong>{" "}
              {actionCategories.find((c) => c.value === action.actionCategory)?.label || action.actionCategory}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>By:</strong> {action.actedByName} (#{action.actedBy})
          </Typography>
          <Typography variant="body2">
            <strong>At:</strong> <Time formatExact="MMM DD, YYYY HH:mm">{action.actedAt}</Time>
          </Typography>
        </div>
        <div className={classes.message} style={{ marginTop: 8 }}>
          {action.actionDescription}
        </div>
      </div>
    );
  };

  const renderPendingForm = () => (
    <>
      {mcpTokenError && needsMcpToken && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          Unable to connect to the AI service. This action path is unavailable.
        </Alert>
      )}

      {isProposalActive && (
        <Box className={classes.proposalBanner}>
          <AssistantOutlined
            fontSize="small"
            className={classes.proposalBannerIcon}
          />
          <Typography variant="body2" className={classes.proposalBannerText}>
            Application Support Agent proposed this action
            {isEdited ? " (edited)" : ""}
          </Typography>
        </Box>
      )}

      {isProposalActive && proposal?.reasoning && (
        <div className={classes.section}>
          <Typography className={classes.label} variant="subtitle2">
            Agent reasoning
          </Typography>
          <div className={classes.message}>{proposal.reasoning}</div>
        </div>
      )}

      <div className={classes.section}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Action Path</FormLabel>
          <RadioGroup
            row
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
          >
            <FormControlLabel
              value="intervention"
              control={<Radio />}
              label="Intervention Taken"
              disabled={submitting || formLocked}
            />
            <FormControlLabel
              value="no_intervention"
              control={<Radio />}
              label="No Intervention"
              disabled={submitting || formLocked}
            />
          </RadioGroup>
        </FormControl>
      </div>

      {actionType === "intervention" && (
        <div className={classes.section}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Action Category</InputLabel>
            <Select
              value={actionCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              label="Action Category"
              disabled={submitting || formLocked}
            >
              {actionCategories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      {inlineError && (
        <Alert severity="warning" className={classes.inlineError}>
          {inlineError}
        </Alert>
      )}

      {!(proposal?.category === "no_intervention" && formLocked) && renderInterventionBody()}
    </>
  );

  const renderActionButtons = (isPending) => {
    if (!isPending) return null;
    if (proposal && !editMode) {
      const acceptLabel =
        proposal.category === "no_intervention" ? "Confirm — no action needed" : "Accept";
      return (
        <>
          <Button onClick={() => setEditMode(true)} disabled={submitting}>
            Edit
          </Button>
          <Button
            onClick={handleAccept}
            color="primary"
            variant="contained"
            disabled={
              submitting ||
              (needsMcpToken && (mcpTokenError || !mcpToken))
            }
          >
            {submitting ? <CircularProgress size={20} /> : acceptLabel}
          </Button>
        </>
      );
    }
    return (
      <Button
        onClick={handleSubmit}
        color="primary"
        variant="contained"
        disabled={!isValid || submitting}
      >
        {submitting ? <CircularProgress size={20} /> : "Submit"}
      </Button>
    );
  };

  if (!alert)
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );

  const isPending = currentAlert.status === "pending_ops_review";
  const action = currentAlert.action;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {renderDialogTitle()}
      <DialogContent dividers>
        <div className={classes.section}>
          <Typography className={classes.label} variant="subtitle2">
            Applicant Message
          </Typography>
          <div className={classes.message}>{alert.originalMessage}</div>
        </div>

        {alert.sentimentSummary && (
          <div className={classes.section}>
            <Typography className={classes.label} variant="subtitle2">
              Sentiment Summary
            </Typography>
            <Typography variant="body2">{alert.sentimentSummary}</Typography>
          </div>
        )}

        {renderKeyConcerns()}

        <div className={classes.section}>
          <Typography className={classes.label} variant="subtitle2">
            Context
          </Typography>
          <div className={classes.context}>
            <Typography variant="body2">
              <strong>Partner:</strong>{" "}
              <a
                href={`/partners/${alert.partnerId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                {alert.partnerName} (#{alert.partnerId})
              </a>
            </Typography>
            <Typography variant="body2">
              <strong>Applicant:</strong>{" "}
              <a
                href={`/applicants/${alert.applicantId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                {alert.applicantName} (#{alert.applicantId})
              </a>
            </Typography>
            <Typography variant="body2">
              <strong>Application:</strong>{" "}
              <a
                href={`/applications/${alert.applicationId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                #{alert.applicationId}
              </a>
            </Typography>
            <Typography variant="body2">
              <strong>Chat:</strong>{" "}
              <a
                href={`${config.portalHost}/applications/${alert.applicationId}/reports?applicantId=${alert.applicantId}&report=chat`}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                Open in Portal
              </a>
            </Typography>
          </div>
        </div>

        {!isPending && renderTakenAction(action)}

        {isPending && refreshError && (
          <Alert severity="error" className={classes.inlineError}>
            {refreshError}
          </Alert>
        )}
        {isPending && (isPolling ? (
          <Box style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderPendingForm()
        ))}
      </DialogContent>
      <DialogActions style={{ justifyContent: "space-between" }}>
        <div>
          {isPending && (
            <Button
              onClick={handleRefresh}
              disabled={isPolling || submitting || !mcpToken}
              startIcon={isPolling ? <CircularProgress size={16} /> : <Refresh />}
            >
              Refresh
            </Button>
          )}
        </div>
        <div>
          <Button onClick={onClose} disabled={submitting}>
            {isPending ? "Cancel" : "Close"}
          </Button>
          {!isPolling && renderActionButtons(isPending)}
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ActionDialog;
